import React, { useState } from 'react';
import { FaEdit, FaTrashAlt } from 'react-icons/fa';
import { 
  capitalizeRelationship, 
  formatDOB, 
  calculateAgeFromDob, 
  capitalizeGender,
  normalizeRelationshipForApi
} from '../../utils/subscriberHelpers';
import Pagination from '../common/Pagination';

const PAGE_SIZE = 25;

const FamilyMembersList = ({
  family,
  isFinance,
  isSupport,
  onEdit,
  onDeactivate,
  existingSingletonRelations
}) => {
  const [familyPage, setFamilyPage] = useState(1);
  
  // Safeguard: ensure family is an array
  const familyArray = Array.isArray(family) ? family : [];
  
  const familyTotalPages = Math.max(1, Math.ceil(familyArray.length / PAGE_SIZE));
  const familyStartIdx = (familyPage - 1) * PAGE_SIZE;
  const familyPageRows = familyArray.slice(familyStartIdx, familyStartIdx + PAGE_SIZE);

  return (
      <div className="p-4 sm:p-6">
        <div className="overflow-x-auto">
          <table className="w-full border border-gray-200 rounded-md min-w-max">
            <thead className="bg-gray-50">
              <tr className="border-b border-gray-200">
                <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider leading-tight whitespace-nowrap">Status</th>
                <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider leading-tight whitespace-nowrap">Name</th>
                <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider leading-tight whitespace-nowrap">Relationship</th>
                <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider leading-tight whitespace-nowrap">DOB</th>
                <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider leading-tight whitespace-nowrap">Age</th>
                <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider leading-tight whitespace-nowrap">Gender</th>
                <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider leading-tight whitespace-nowrap">Mobile</th>
                <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider leading-tight whitespace-nowrap">Email</th>
                <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider leading-tight whitespace-nowrap">Member ID</th>
                {!isFinance && !isSupport && (
                  <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider leading-tight whitespace-nowrap">ACTIONS</th>
                )}
              </tr>
            </thead>
            <tbody>
              {familyArray.length === 0 ? (
                <tr>
                  <td colSpan={!isFinance && !isSupport ? 10 : 9} className="px-3 sm:px-6 py-8 text-center text-sm text-gray-500">
                    No family members found
                  </td>
                </tr>
              ) : (
                familyPageRows.map((m) => {
                const isInactive = m.status === 'Inactive' || m.status === 'inactive';
                const isSelf = normalizeRelationshipForApi(m.relationship || m.relation || '') === 'self';
                
                return (
                  <tr key={m.member_id || m.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-3 sm:px-6 py-2 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        (m.status === 'Active' || m.status === 'active') ? 'bg-green-600 text-white' : 
                        (m.status === 'Inactive' || m.status === 'inactive') ? 'bg-gray-600 text-white' : 
                        'bg-yellow-600 text-white'
                      }`}>
                        {m.status || 'N/A'}
                      </span>
                    </td>
                    <td className="px-3 sm:px-6 py-2 text-xs text-gray-700 leading-tight whitespace-nowrap">{m?.name || 'N/A'}</td>
                    <td className="px-3 sm:px-6 py-2 whitespace-nowrap">
                      <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700">
                        {capitalizeRelationship(m.relationship || m.relation)}
                      </span>
                    </td>
                    <td className="px-3 sm:px-6 py-2 text-xs text-gray-700 leading-tight whitespace-nowrap">
                      {formatDOB(m.dob || m.date_of_birth || m.dateOfBirth)}
                    </td>
                    <td className="px-3 sm:px-6 py-2 text-xs text-gray-700 leading-tight whitespace-nowrap">
                      {m.age ?? calculateAgeFromDob(m.dob)}
                    </td>
                    <td className="px-3 sm:px-6 py-2 text-xs text-gray-700 leading-tight whitespace-nowrap">
                      {capitalizeGender(m.gender)}
                    </td>
                    <td className="px-3 sm:px-6 py-2 text-xs text-gray-700 leading-tight whitespace-nowrap">
                      {m.contact_number || m.contact || m.mobile || m.phone || 'N/A'}
                    </td>
                    <td className="px-3 sm:px-6 py-2 text-xs text-gray-700 leading-tight whitespace-nowrap">
                      {m.email || 'N/A'}
                    </td>
                    <td className="px-3 sm:px-6 py-2 font-mono text-xs text-gray-900 leading-tight whitespace-nowrap">
                      {m.member_id || m.id || 'N/A'}
                    </td>
                    {!isFinance && !isSupport && (
                      <td className="px-3 sm:px-6 py-2 whitespace-nowrap">
                        <div className="flex items-center space-x-3 text-gray-500">
                          <button 
                            onClick={() => !isInactive && onEdit(m)} 
                            disabled={isInactive}
                            className={`p-1 rounded transition-colors ${
                              isInactive 
                                ? 'text-gray-300 cursor-not-allowed opacity-50' 
                                : 'hover:bg-blue-600 hover:text-white'
                            }`}
                            title={isInactive ? 'Cannot edit inactive member' : 'Edit Member'}
                          >
                            <FaEdit />
                          </button>
                          <button 
                            onClick={() => {
                              if (!isInactive && !isSelf) {
                                onDeactivate(m);
                              }
                            }} 
                            disabled={isInactive || isSelf}
                            className={`p-1 rounded transition-colors ${
                              (isInactive || isSelf)
                                ? 'text-gray-300 cursor-not-allowed opacity-50' 
                                : 'text-red-500 hover:bg-red-500 hover:text-white'
                            }`}
                            title={
                              isSelf 
                                ? 'Cannot deactivate Self relationship' 
                                : (isInactive ? 'Member already inactive' : 'Deactivate')
                            }
                          >
                            <FaTrashAlt />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })
              )}
            </tbody>
          </table>
        </div>
        <Pagination
          currentPage={familyPage}
          totalPages={familyTotalPages}
          totalItems={familyArray.length}
          startIdx={familyStartIdx}
          pageSize={PAGE_SIZE}
          onPageChange={setFamilyPage}
        />
    </div>
  );
};

export default FamilyMembersList;

